import { bool, number, string, shape, func } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withRouter } from 'next/router';
import RecipeStar from 'components/RecipeStar';
import Tooltip from 'components/Tooltip';
import { getFormattedDate } from 'oxygen-lib-ui/helpers/date';
import { baseStyles, starStyles } from './styles';

export const MAX_RATING = 5;

const RATED_COLOR = '#ff881f';

const RECIPE_RATING = 'rating-pre-redirect';
const setUnauthedRating = (rating, url) =>
  window.localStorage.setItem(RECIPE_RATING, JSON.stringify({ rating, url }));
const getUnauthedRating = () => window.localStorage.getItem(RECIPE_RATING);
const removeUnauthedRating = () =>
  window.localStorage.removeItem(RECIPE_RATING);

export class RecipeRating extends Component {
  constructor(props) {
    super(props);
    this.state = {
      userRating: null,
      userRatingDateSeconds: null,
      showPopup: false
    };
    this.handleRateRecipe = this.handleRateRecipe.bind(this);
    this.handleStarClick = this.handleStarClick.bind(this);
    this.renderStars = this.renderStars.bind(this);
    this.togglePopup = this.togglePopup.bind(this);
  }

  componentDidMount() {
    if (!this.props.user) {
      return;
    }

    const savedUserRating = JSON.parse(getUnauthedRating());
    const location = new URL(window.location);
    const params = location.searchParams;
    const showRating = params.get('show-rating');

    // Recipe was rated while user was unauthed
    if (
      showRating === 'true' &&
      savedUserRating !== null &&
      savedUserRating.url &&
      savedUserRating.url.includes(location.pathname)
    ) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState(
        {
          userRating: parseInt(savedUserRating.rating, 10)
        },
        this.handleRateRecipe
      );
      removeUnauthedRating();
    } else {
      // Check if user has rated this recipe
      this.props.fetchUserRating().then(({ data }) => {
        if (data && data.getUserRating) {
          this.setState({
            userRating: data.getUserRating.rating,
            userRatingDateSeconds:
              data.getUserRating.updatedAt || data.getUserRating.createdAt
          });
        }
      });
    }
  }

  handleStarClick(e) {
    if ((e.key === 'Enter' && e.type === 'keypress') || e.type === 'click') {
      const pos = parseInt(e.currentTarget.getAttribute('data-pos'), 10);
      this.setState({
        userRating: pos
      });
    }
  }

  handleRateRecipe() {
    if (this.props.rateRecipeIsLoading) {
      return;
    }

    const { userRating } = this.state;
    const { recipeId, rateRecipe, router, user } = this.props;

    if (!user) {
      setUnauthedRating(userRating, window.location.href);
      const url = new URL(window.location);
      url.searchParams.append('show-rating', 'true');
      url.hash = `post-recipe-${recipeId}`;
      router.push(
        `/sign-in?redirect_uri=${encodeURIComponent(url.toString())}`
      );
    } else {
      rateRecipe(userRating).then(res => {
        const {
          rate: {
            rating: { rating }
          }
        } = res.data;
        this.setState({
          userRating: rating,
          userRatingDateSeconds: Date.now() / 1000,
          showPopup: false
        });
      });
    }
  }

  togglePopup(e) {
    e.preventDefault();
    this.setState({
      showPopup: !this.state.showPopup
    });
  }

  renderStars({ isInteractive = false } = {}) {
    const { userRating } = this.state;
    const {
      ratingsData: { average },
      placement
    } = this.props;
    // If the stars are interactive, or user rating is set, we should
    // show the user rating. Otherwise, show the average.
    const rating =
      isInteractive || userRating || placement === 'recipeBox'
        ? userRating || 0
        : average;
    const stars = new Array(MAX_RATING).fill(null).map((_, i) => {
      const pos = i + 1;
      const star = (
        <RecipeStar rating={rating} pos={pos} fillColor={RATED_COLOR} />
      );
      return (
        <li
          className={`RecipeRating__star RecipeRating__star--${pos}`}
          key={pos}
        >
          {isInteractive ? (
            <button
              className="RecipeRating__star-icon"
              data-pos={pos}
              onClick={this.handleStarClick}
              onKeyPress={this.handleStarClick}
              aria-label={`recipe rating ${pos} star`}
            >
              {star}
            </button>
          ) : (
            star
          )}
          <style jsx>{starStyles}</style>
        </li>
      );
    });
    return stars.reverse();
  }

  render() {
    const { userRating, userRatingDateSeconds } = this.state;
    const { ratingsData, rateRecipeIsLoading, placement } = this.props;

    if (ratingsData === undefined) {
      return null;
    }

    const { totalRatings } = ratingsData;

    const ratingClasses = classnames('RecipeRating', {
      'RecipeRating--reader-rating': userRating
    });

    return (
      <div className={ratingClasses}>
        <div className="RecipeRating__rating">
          {placement !== 'recipeBox' && (
            <>
              {userRatingDateSeconds ? (
                <span className="RecipeRating__text">
                  Rated {getFormattedDate(userRatingDateSeconds, '%D')}
                </span>
              ) : (
                <span className="RecipeRating__text">
                  {totalRatings} {totalRatings === 1 ? 'Rating' : 'Ratings'}
                </span>
              )}
            </>
          )}
          {placement === 'recipeBox' && !userRatingDateSeconds && (
            <span className="RecipeRating__text RecipeRating__rateNow">
              RATE NOW
            </span>
          )}
          <a href="#rate-recipe" onClick={this.togglePopup}>
            <ul className="RecipeRating__stars">{this.renderStars()}</ul>
          </a>
          {placement === 'recipeBox' && userRatingDateSeconds && (
            <>
              {userRatingDateSeconds ? (
                <span className="RecipeRating__text">
                  Rated {getFormattedDate(userRatingDateSeconds, '%b %d, %Y')}
                </span>
              ) : (
                <span className="RecipeRating__text">Not rated</span>
              )}
            </>
          )}
        </div>
        {this.state.showPopup && (
          <div className="RecipeRating__anchor">
            <Tooltip location="bottom">
              <div className="RecipeRating__popup">
                <p>Rate this recipe:</p>
                <ul className="RecipeRating__stars">
                  {this.renderStars({ isInteractive: true })}
                </ul>
                <div className="RecipeRating__buttons">
                  <button
                    className="RecipeRating__cancel button"
                    onClick={this.togglePopup}
                  >
                    Cancel
                  </button>
                  <button
                    className="RecipeRating__rate button"
                    data-gtm-recipe-rating={userRating}
                    data-gtm-recipe-rating-placement={placement}
                    onClick={this.handleRateRecipe}
                    disabled={!userRating || rateRecipeIsLoading}
                  >
                    Rate it
                  </button>
                </div>
              </div>
            </Tooltip>
          </div>
        )}
        <style jsx>{baseStyles}</style>
      </div>
    );
  }
}

RecipeRating.propTypes = {
  user: shape({
    // TBD
  }),
  router: shape({
    asPath: string
  }),
  ratingsData: shape({
    average: number,
    totalRatings: number
  }),
  rateRecipe: func,
  fetchUserRating: func,
  rateRecipeIsLoading: bool,
  recipeId: number.isRequired,
  placement: string
};

RecipeRating.defaultProps = {
  router: undefined,
  ratingsData: undefined,
  user: undefined,
  rateRecipe: () => Promise.resolve({}),
  fetchUserRating: () => Promise.resolve({}),
  rateRecipeIsLoading: false,
  placement: undefined
};

const mapStateToProps = ({ user }) => ({ user });

export default connect(mapStateToProps)(withRouter(RecipeRating));
